// Utilitaires pour les calculs solaires SUNLIFE

// Données de production par région en France (kWh/kWc/an)
const PRODUCTION_BY_REGION = {
  nord: 950,
  centre: 1100,
  ouest: 1150,
  est: 1200,
  'sud-ouest': 1300,
  'sud-est': 1400,
  corse: 1500,
};

// Estimation de la région basée sur l'adresse
function estimateRegionFromAddress(address) {
  if (!address) return 'centre';
  
  const addressLower = address.toLowerCase();
  
  if (addressLower.includes('lille') || addressLower.includes('nord') || addressLower.includes('calais')) {
    return 'nord';
  }
  if (addressLower.includes('paris') || addressLower.includes('orleans')) {
    return 'centre';
  }
  if (addressLower.includes('rennes') || addressLower.includes('nantes') || addressLower.includes('brest')) {
    return 'ouest';
  }
  if (addressLower.includes('strasbourg') || addressLower.includes('metz') || addressLower.includes('dijon')) {
    return 'est';
  }
  if (addressLower.includes('bordeaux') || addressLower.includes('toulouse')) {
    return 'sud-ouest';
  }
  if (addressLower.includes('marseille') || addressLower.includes('nice') || addressLower.includes('lyon')) {
    return 'sud-est';
  }
  if (addressLower.includes('corse') || addressLower.includes('ajaccio')) {
    return 'corse';
  }
  
  return 'centre';
}

// Estimation du coût d'installation basé sur la puissance
export function estimateInstallationCost(powerKwc) {
  const baseCostPerKwc = powerKwc <= 3 ? 2800 :
                         powerKwc <= 6 ? 2400 :
                         powerKwc <= 9 ? 2200 : 2000;
  return powerKwc * baseCostPerKwc;
}

// Calcul des aides (CEE + MaPrimeRénov)
export function calculateSubsidies(powerKwc, revenuFiscal = 0) {
  // CEE : environ 150-250€/kWc
  const cee = powerKwc * 180;
  
  // MaPrimeRénov (selon revenus)
  let maprimerenov = 0;
  if (revenuFiscal > 0 && revenuFiscal < 30000) {
    maprimerenov = Math.min(powerKwc * 400, 3000);
  } else if (revenuFiscal >= 30000 && revenuFiscal < 50000) {
    maprimerenov = Math.min(powerKwc * 300, 2000);
  } else if (revenuFiscal >= 50000 && revenuFiscal < 80000) {
    maprimerenov = Math.min(powerKwc * 200, 1500);
  }
  
  return cee + maprimerenov;
}

// Calcul financier complet
export function calculateSolarFinancials(params) {
  const {
    installationPowerKwc,
    installationCostEuros,
    subsidiesEuros,
    currentElectricityPricePerKwh = 0.2016,
    projectLifetimeYears = 25,
    location,
  } = params;

  // 1. Production annuelle selon la région
  let productionPerKwc = 1100;
  if (location?.address) {
    const region = estimateRegionFromAddress(location.address);
    productionPerKwc = PRODUCTION_BY_REGION[region];
  }
  
  const annualProductionKwh = installationPowerKwc * productionPerKwc;
  
  // 2. Coûts
  const netInstallationCost = installationCostEuros - subsidiesEuros;
  const costPerKwc = netInstallationCost / installationPowerKwc;
  
  // 3. Calcul des économies avec inflation 7%/an
  const ELECTRICITY_INFLATION_RATE = 0.07;
  const annualSavingsWithInflation = [];
  const yearlyBreakdown = [];
  
  let cumulativeSavings = 0;
  let currentElectricityPrice = currentElectricityPricePerKwh;
  
  for (let year = 1; year <= Math.min(projectLifetimeYears, 20); year++) {
    // Dégradation panneaux : -0.5%/an
    const productionDegradation = Math.pow(0.995, year - 1);
    const yearlyProduction = annualProductionKwh * productionDegradation;
    
    // Prix électricité avec inflation
    currentElectricityPrice = currentElectricityPricePerKwh * Math.pow(1 + ELECTRICITY_INFLATION_RATE, year - 1);
    
    // Économies annuelles
    const yearlyEconomies = yearlyProduction * currentElectricityPrice;
    annualSavingsWithInflation.push(yearlyEconomies);
    cumulativeSavings += yearlyEconomies;
    
    yearlyBreakdown.push({
      year,
      electricityPrice: currentElectricityPrice,
      annualSavings: yearlyEconomies,
      cumulativeSavings,
      production: yearlyProduction,
    });
  }
  
  // 4. Calcul du ROI et période de retour
  const cumulativeSavings20Years = cumulativeSavings;
  const roi20Years = ((cumulativeSavings20Years - netInstallationCost) / netInstallationCost) * 100;
  
  // Période de retour sur investissement
  let paybackPeriodYears = 0;
  for (let i = 0; i < yearlyBreakdown.length; i++) {
    if (yearlyBreakdown[i].cumulativeSavings >= netInstallationCost) {
      paybackPeriodYears = i + 1;
      break;
    }
  }
  
  if (paybackPeriodYears === 0) {
    paybackPeriodYears = projectLifetimeYears;
  }
  
  const annualSavingsEuros = annualSavingsWithInflation[0] || 0;
  const netProfitability20Years = cumulativeSavings20Years - netInstallationCost;
  
  return {
    annualProductionKwh,
    productionPerKwc,
    netInstallationCost,
    costPerKwc,
    annualSavingsEuros,
    annualSavingsWithInflation,
    cumulativeSavings20Years,
    roi20Years,
    paybackPeriodYears,
    netProfitability20Years,
    yearlyBreakdown,
  };
}
